package com.evoapps.evoshift.domain.team

import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.repository.EmployeeTimelineRepository
import com.evoapps.evoshift.domain.repository.RoleSkillsUpdate
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.TeamBulkRepository
import com.evoapps.evoshift.domain.timeline.EmployeeTimelineUseCase
import com.evoapps.evoshift.domain.timeline.RecomputeTrigger
import com.evoapps.evoshift.domain.timeline.TimelineEdit
import kotlinx.coroutines.flow.first
import kotlinx.datetime.LocalDate

/** SH-269: what Undo puts back: the timelines' inverse edits and the positions and skills before. */
data class BulkUndo(
    /** (workplaceId, userId) to the edits that undo theirs, newest first. */
    val timeline: Map<Pair<String, String>, List<TimelineEdit>> = emptyMap(),
    /** workplaceId to the positions and skills as they were. */
    val roles: Map<String, List<RoleSkillsUpdate>> = emptyMap()
) {
    val isEmpty: Boolean get() = timeline.values.all { it.isEmpty() } && roles.values.all { it.isEmpty() }
}

/** What a bulk change did: people and workplaces changed, those that failed, and its Undo. */
data class BulkResult(
    val people: Int,
    val workplaces: Int,
    /** Workplaces whose writes failed (nothing of theirs changed), with the reason. */
    val failed: Map<String, Throwable> = emptyMap(),
    val undo: BulkUndo = BulkUndo()
)

/**
 * SH-269: reads what a bulk change needs (the closed months of each workplace) and applies a plan:
 * per workplace, the positions and skills in one batch and the timeline edits (pay, contract, end)
 * of all its people in one batch, then the open months repriced from the earliest day changed.
 * A workplace that fails leaves the others applied; Undo covers what was written.
 */
class BulkEmployeeEditUseCase(
    private val timeline: EmployeeTimelineUseCase,
    private val timelines: EmployeeTimelineRepository,
    private val team: TeamBulkRepository,
    private val rosters: RosterRepository
) {
    /** The months ("YYYY-MM") of [workplaceId] whose roster is LOCKED or ARCHIVED (back-dating stops there). */
    suspend fun closedMonths(workplaceId: String): Set<String> =
        runCatching { rosters.observeRosters(workplaceId).first() }.getOrDefault(emptyList())
            .filter { it.status == RosterStatus.LOCKED || it.status == RosterStatus.ARCHIVED }
            .mapTo(mutableSetOf()) { it.getMonthYear() }

    suspend fun apply(plan: BulkPlan, editor: User): BulkResult {
        val failed = mutableMapOf<String, Throwable>()
        val timelineUndo = mutableMapOf<Pair<String, String>, List<TimelineEdit>>()
        val roleUndo = mutableMapOf<String, List<RoleSkillsUpdate>>()
        val done = mutableListOf<BulkLine>()
        plan.changing.groupBy { it.target.workplaceId }.forEach { (workplaceId, lines) ->
            runCatching {
                // The timeline's edits first, prepared (and checked) for everyone before anything is written.
                val prepared = lines.filter { line -> line.changes.any { it.field.viaTimeline } }.map { line ->
                    line to timeline.prepare(workplaceId, line.target.userId, editor) { BulkTimelineEdits.edits(it, plan.edit, line) }.getOrThrow()
                }.filterNot { it.second.isEmpty }
                val roles = lines.mapNotNull { line ->
                    val role = (line.change(BulkField.ROLE) as? BulkChange.Role)
                    val skills = (line.change(BulkField.SKILLS) as? BulkChange.Skills)
                    if (role == null && skills == null) null
                    else RoleSkillsUpdate(line.target.userId, role?.after, skills?.after) to
                        RoleSkillsUpdate(line.target.userId, role?.let { it.before.orEmpty() }, skills?.before)
                }
                if (roles.isNotEmpty()) team.updateRolesAndSkills(workplaceId, roles.map { it.first }).getOrThrow()
                if (prepared.isNotEmpty()) {
                    timelines.saveAll(prepared.map { it.second.write }).onFailure { e ->
                        // All or nothing per workplace: the positions go back too.
                        if (roles.isNotEmpty()) team.updateRolesAndSkills(workplaceId, roles.map { it.second })
                        throw e
                    }
                }
                roleUndo[workplaceId] = roles.map { it.second }
                prepared.forEach { (line, p) ->
                    timelineUndo[workplaceId to line.target.userId] = BulkTimelineEdits.inverse(p.before, p.applied)
                }
                reprice(workplaceId, prepared.mapNotNull { (_, p) -> p.saved.recompute?.let { it to p.before.hire?.from } })
                done += lines
            }.onFailure { failed[workplaceId] = it }
        }
        return BulkResult(
            people = done.map { it.target.userId }.distinct().size,
            workplaces = done.map { it.target.workplaceId }.distinct().size,
            failed = failed,
            undo = BulkUndo(timelineUndo, roleUndo)
        )
    }

    /** Puts back what [undo] holds, workplace by workplace (the same batches as the change). */
    suspend fun undo(undo: BulkUndo, editor: User): Result<Unit> = runCatching {
        val workplaces = (undo.timeline.keys.map { it.first } + undo.roles.keys).distinct()
        workplaces.forEach { workplaceId ->
            val prepared = undo.timeline.filterKeys { it.first == workplaceId }.filterValues { it.isNotEmpty() }.map { (key, edits) ->
                timeline.prepare(workplaceId, key.second, editor) { edits }.getOrThrow()
            }
            undo.roles[workplaceId]?.takeIf { it.isNotEmpty() }?.let { team.updateRolesAndSkills(workplaceId, it).getOrThrow() }
            if (prepared.isNotEmpty()) timelines.saveAll(prepared.map { it.write }).getOrThrow()
            reprice(workplaceId, prepared.mapNotNull { p -> p.saved.recompute?.let { it to p.before.hire?.from } })
        }
    }

    /** The open months from the earliest day any of [triggers] changes (a change "from the start": from the hire). */
    private suspend fun reprice(workplaceId: String, triggers: List<Pair<RecomputeTrigger, LocalDate?>>) {
        if (triggers.isEmpty()) return
        val from = triggers.map { (trigger, hire) -> trigger.from ?: hire ?: EARLIEST }.min()
        timeline.recompute(workplaceId, RecomputeTrigger(from), null)
    }

    private companion object {
        val EARLIEST = LocalDate(2000, 1, 1)
    }
}
